import { useState, useEffect } from 'preact/hooks';
import { MainLayout } from '../../components/MainLayout';
import { useScopeContext } from '../../contexts/ScopeContext';
import { getAuthHeaders } from '../../utils/auth';

interface PaymentInfo {
  status: 'none' | 'required' | 'active' | 'past_due' | 'cancelled';
  provider?: string;
  hasPaymentMethod: boolean;
  nextBillingAt?: string;
  lastPaymentAt?: string;
}

interface TeamAccessRequirements {
  requiresPayment: boolean;
  canCreateTeam: boolean;
  canInviteMembers: boolean;
  canUseTeamFeatures: boolean;
  maxTeamMembers: number;
}

export function BillingPage() {
  useScopeContext();
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [requirements, setRequirements] = useState<TeamAccessRequirements | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentInfo();
  }, []);

  const loadPaymentInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL}/payment/info`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentInfo(data.paymentInfo);
        setRequirements(data.requirements);
      } else {
        // Default to requiring payment setup
        setPaymentInfo({
          status: 'required',
          hasPaymentMethod: false
        });
        setRequirements({
          requiresPayment: true,
          canCreateTeam: true,
          canInviteMembers: false,
          canUseTeamFeatures: false,
          maxTeamMembers: 1
        });
      }
    } catch (err) {
      console.error('Error loading payment info:', err);
      setError('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPayment = async () => {
    try {
      setSetupLoading(true);
      setError(null);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/payment/setup-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          returnUrl: window.location.href
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate payment setup URL');
      }

      const data = await response.json();
      window.location.href = data.setupUrl;
    } catch (err) {
      console.error('Error setting up payment:', err);
      setError(err instanceof Error ? err.message : 'Failed to set up payment');
    } finally {
      setSetupLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span class="badge badge-success">Active</span>;
      case 'past_due':
        return <span class="badge badge-warning">Past Due</span>;
      case 'cancelled':
        return <span class="badge badge-error">Cancelled</span>;
      case 'required':
        return <span class="badge badge-warning">Setup Required</span>;
      default:
        return <span class="badge badge-ghost">None</span>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-4xl">
            <div class="flex items-center justify-center h-64">
              <span class="loading loading-spinner loading-lg"></span>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
        <div class="container mx-auto p-8 max-w-4xl">
        <div class="mb-8">
          <h1 class="text-3xl font-bold">
            Team Billing & Payment
          </h1>
          <p class="text-base-content/70 mt-2">
            Manage team billing, payment methods and features
          </p>
        </div>

        {error && (
          <div class="alert alert-error mb-6">
            <span>{error}</span>
          </div>
        )}

        {/* Payment Status */}
        <div class="card bg-base-100 shadow-md mb-6">
          <div class="card-body">
            <div class="flex justify-between items-start mb-4">
              <h2 class="card-title">Payment Status</h2>
              {paymentInfo && getStatusBadge(paymentInfo.status)}
            </div>

            {paymentInfo && (
              <div class="grid gap-4">
                <div class="flex justify-between">
                  <span class="text-base-content/70">Payment Method:</span>
                  <span>{paymentInfo.hasPaymentMethod ? 'Configured' : 'Not Set Up'}</span>
                </div>

                {paymentInfo.provider && (
                  <div class="flex justify-between">
                    <span class="text-base-content/70">Provider:</span>
                    <span class="capitalize">{paymentInfo.provider}</span>
                  </div>
                )}

                {paymentInfo.nextBillingAt && (
                  <div class="flex justify-between">
                    <span class="text-base-content/70">Next Billing:</span>
                    <span>{formatDate(paymentInfo.nextBillingAt)}</span>
                  </div>
                )}

                {paymentInfo.lastPaymentAt && (
                  <div class="flex justify-between">
                    <span class="text-base-content/70">Last Payment:</span>
                    <span>{formatDate(paymentInfo.lastPaymentAt)}</span>
                  </div>
                )}
              </div>
            )}

            {paymentInfo && !paymentInfo.hasPaymentMethod && (
              <div class="mt-6">
                <button
                  class="btn btn-primary"
                  onClick={handleSetupPayment}
                  disabled={setupLoading}
                >
                  {setupLoading ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      Setting up...
                    </>
                  ) : (
                    'Set Up Payment Method'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Team Features */}
        <div class="card bg-base-100 shadow-md mb-6">
          <div class="card-body">
            <h2 class="card-title mb-4">Team Features</h2>

            {requirements && (
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <span>Create teams</span>
                  <span class={`text-lg ${requirements.canCreateTeam ? 'text-success' : 'text-error'}`}>
                    {requirements.canCreateTeam ? '✅' : '❌'}
                  </span>
                </div>

                <div class="flex items-center justify-between">
                  <span>Invite team members</span>
                  <span class={`text-lg ${requirements.canInviteMembers ? 'text-success' : 'text-error'}`}>
                    {requirements.canInviteMembers ? '✅' : '❌'}
                  </span>
                </div>

                <div class="flex items-center justify-between">
                  <span>Advanced team features</span>
                  <span class={`text-lg ${requirements.canUseTeamFeatures ? 'text-success' : 'text-error'}`}>
                    {requirements.canUseTeamFeatures ? '✅' : '❌'}
                  </span>
                </div>

                <div class="flex items-center justify-between">
                  <span>Max team members</span>
                  <span class="font-semibold">{requirements.maxTeamMembers}</span>
                </div>
              </div>
            )}

            {requirements && requirements.requiresPayment && !paymentInfo?.hasPaymentMethod && (
              <div class="mt-6 p-4 bg-warning/10 rounded-lg">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-warning text-lg">⚠️</span>
                  <span class="font-semibold">Limited Access</span>
                </div>
                <p class="text-sm text-base-content/70">
                  Set up your payment method to unlock full team collaboration features
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Feature Comparison */}
        <div class="card bg-base-100 shadow-md">
          <div class="card-body">
            <h2 class="card-title mb-4">Feature Comparison</h2>

            <div class="overflow-x-auto">
              <table class="table table-zebra">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free</th>
                    <th>With Payment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Create teams</td>
                    <td class="text-success">✅</td>
                    <td class="text-success">✅</td>
                  </tr>
                  <tr>
                    <td>Team members</td>
                    <td>1 member</td>
                    <td>Unlimited</td>
                  </tr>
                  <tr>
                    <td>Invite members</td>
                    <td class="text-error">❌</td>
                    <td class="text-success">✅</td>
                  </tr>
                  <tr>
                    <td>Team permissions</td>
                    <td class="text-error">❌</td>
                    <td class="text-success">✅</td>
                  </tr>
                  <tr>
                    <td>Team chat</td>
                    <td class="text-error">❌</td>
                    <td class="text-success">✅</td>
                  </tr>
                  <tr>
                    <td>Shared connections</td>
                    <td class="text-error">❌</td>
                    <td class="text-success">✅</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {!paymentInfo?.hasPaymentMethod && (
              <div class="mt-6 text-center">
                <button
                  class="btn btn-primary btn-lg"
                  onClick={handleSetupPayment}
                  disabled={setupLoading}
                >
                  {setupLoading ? (
                    <>
                      <span class="loading loading-spinner loading-sm"></span>
                      Setting up...
                    </>
                  ) : (
                    'Unlock Team Features'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </MainLayout>
  );
}
